import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, X, Trash2, AlertCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function JobModal({ open, onClose, onSaved, stages, customers, editing }) {
    const [form, setForm] = useState({
        title: "",
        customer_id: "",
        customer_name: "",
        department: "",
        quantity: "",
        notes: "",
        priority: "normal",
        deadline: "",
        status_id: "",
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (editing) {
            setForm({
                title: editing.title || "",
                customer_id: editing.customer_id || "",
                customer_name: "",
                department: editing.department || "",
                quantity: editing.quantity || "",
                notes: editing.notes || "",
                priority: editing.priority || "normal",
                deadline: editing.deadline || "",
                status_id: editing.status_id || "",
            });
        } else {
            setForm({
                title: "", customer_id: "", customer_name: "", department: "",
                quantity: "", notes: "", priority: "normal", deadline: "", status_id: ""
            });
        }
        setError("");
    }, [editing, open]);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const payload = { ...form };
            if (payload.quantity) payload.quantity = parseInt(payload.quantity);
            else delete payload.quantity;
            if (!payload.customer_id) delete payload.customer_id;
            if (!payload.deadline) delete payload.deadline;
            if (!payload.status_id) delete payload.status_id;
            if (editing) {
                await api.put(`/jobs/${editing.id}`, payload);
            } else {
                await api.post("/jobs", payload);
            }
            onSaved();
            onClose();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const field = (n, v) => setForm((p) => ({ ...p, [n]: v }));

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(13,20,33,.5)" }} onClick={onClose} data-testid="job-modal">
            <div className="card-tpl w-full max-w-lg p-7" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-5">
                    <h3 className="text-[20px] font-extrabold tracking-tight" style={{ color: "var(--navy)" }}>
                        {editing ? "Modifica commessa" : "Nuova commessa"}
                    </h3>
                    <button onClick={onClose} className="w-8 h-8 rounded-md grid place-items-center hover:bg-stone-100" data-testid="modal-close">
                        <X size={16} />
                    </button>
                </div>

                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <label className="block mono text-[10px] uppercase tracking-[0.08em] font-bold mb-1" style={{ color: "var(--muted)" }}>Titolo *</label>
                        <input data-testid="job-title" required value={form.title} onChange={(e) => field("title", e.target.value)} placeholder="es. Banner 3x2m fiera Salerno" className="input" />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block mono text-[10px] uppercase tracking-[0.08em] font-bold mb-1" style={{ color: "var(--muted)" }}>Cliente</label>
                            <select data-testid="job-customer" value={form.customer_id} onChange={(e) => field("customer_id", e.target.value)} className="input">
                                <option value="">— Seleziona o crea —</option>
                                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block mono text-[10px] uppercase tracking-[0.08em] font-bold mb-1" style={{ color: "var(--muted)" }}>O nuovo cliente</label>
                            <input data-testid="job-customer-quick" value={form.customer_name} onChange={(e) => field("customer_name", e.target.value)} placeholder="Nome cliente nuovo" className="input" />
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="block mono text-[10px] uppercase tracking-[0.08em] font-bold mb-1" style={{ color: "var(--muted)" }}>Reparto</label>
                            <input data-testid="job-department" value={form.department} onChange={(e) => field("department", e.target.value)} placeholder="es. Grande Formato" className="input" />
                        </div>
                        <div>
                            <label className="block mono text-[10px] uppercase tracking-[0.08em] font-bold mb-1" style={{ color: "var(--muted)" }}>Quantità</label>
                            <input data-testid="job-quantity" type="number" value={form.quantity} onChange={(e) => field("quantity", e.target.value)} placeholder="100" className="input" />
                        </div>
                        <div>
                            <label className="block mono text-[10px] uppercase tracking-[0.08em] font-bold mb-1" style={{ color: "var(--muted)" }}>Priorità</label>
                            <select data-testid="job-priority" value={form.priority} onChange={(e) => field("priority", e.target.value)} className="input">
                                <option value="normal">Normale</option>
                                <option value="high">Alta</option>
                                <option value="urgent">Urgente</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block mono text-[10px] uppercase tracking-[0.08em] font-bold mb-1" style={{ color: "var(--muted)" }}>Scadenza</label>
                            <input data-testid="job-deadline" type="date" value={form.deadline} onChange={(e) => field("deadline", e.target.value)} className="input" />
                        </div>
                        {editing && (
                            <div>
                                <label className="block mono text-[10px] uppercase tracking-[0.08em] font-bold mb-1" style={{ color: "var(--muted)" }}>Stato</label>
                                <select data-testid="job-status" value={form.status_id} onChange={(e) => field("status_id", e.target.value)} className="input">
                                    {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block mono text-[10px] uppercase tracking-[0.08em] font-bold mb-1" style={{ color: "var(--muted)" }}>Note</label>
                        <textarea data-testid="job-notes" rows={3} value={form.notes} onChange={(e) => field("notes", e.target.value)} placeholder="Dettagli aggiuntivi..." className="input" />
                    </div>

                    {editing && Array.isArray(editing.files) && editing.files.length > 0 && (
                        <div data-testid="job-files-section">
                            <label className="block mono text-[10px] uppercase tracking-[0.08em] font-bold mb-2" style={{ color: "var(--muted)" }}>📎 File allegati ({editing.files.length})</label>
                            <div className="space-y-1.5">
                                {editing.files.map((f, i) => {
                                    const isDownloadable = !!f.tmp_path;
                                    const isUrl = f.type === "url";
                                    return (
                                        <div key={i} className="flex items-center gap-2 p-2 rounded text-[12.5px]" style={{ background: "var(--bg)", border: "1px solid var(--line)" }} data-testid={`job-file-${i}`}>
                                            <span className="flex-1 truncate font-bold">{f.label || `File ${i + 1}`}</span>
                                            {isUrl && (
                                                <a href={f.label} target="_blank" rel="noreferrer" className="mono text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded" style={{ background: "var(--cobalt-soft)", color: "var(--cobalt)" }}>
                                                    Apri link →
                                                </a>
                                            )}
                                            {isDownloadable && (
                                                <a
                                                    href={`${""}/api/jobs/${editing.id}/files/${i}/download`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="mono text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded text-white"
                                                    style={{ background: "var(--navy)" }}
                                                    data-testid={`job-file-download-${i}`}
                                                >
                                                    Scarica
                                                </a>
                                            )}
                                            {!isDownloadable && !isUrl && (
                                                <span className="mono text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--muted)" }}>{f.type}</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            {editing.files.some(f => f.tmp_path) && (
                                <a
                                    href={`${""}/api/jobs/${editing.id}/files/zip`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded text-[12px] font-bold text-white"
                                    style={{ background: "linear-gradient(135deg, var(--mod-accent), var(--mod-accent-deep))" }}
                                    data-testid="job-files-download-all"
                                >
                                    ⬇ Scarica tutti (ZIP)
                                </a>
                            )}
                            <p className="text-[11px] mt-2" style={{ color: "var(--muted)" }}>
                                I file caricati dal cliente sono disponibili per 24h. Scaricali al più presto.
                            </p>
                        </div>
                    )}

                    {error && (
                        <div className="p-3 rounded-lg text-[13px]" style={{ background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.3)", color: "#B91C1C" }}>{error}</div>
                    )}

                    <div className="flex gap-2 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg font-semibold text-[13px]" style={{ background: "var(--surface)", border: "1px solid var(--line-2)", color: "var(--text)" }}>
                            Annulla
                        </button>
                        <button data-testid="job-submit" type="submit" disabled={loading} className="btn-grad flex-1 py-2.5 rounded-lg font-bold text-[13px]">
                            {loading ? "Salvataggio..." : (editing ? "Salva modifiche" : "Crea commessa")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function Commesse() {
    const [params, setParams] = useSearchParams();
    const [jobs, setJobs] = useState([]);
    const [stages, setStages] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [filter, setFilter] = useState("all");

    const load = async () => {
        const [j, s, c] = await Promise.all([api.get("/jobs"), api.get("/workflow"), api.get("/customers")]);
        setJobs(j.data);
        setStages(s.data);
        setCustomers(c.data);
    };

    useEffect(() => {
        load();
        if (params.get("new") === "1") {
            setEditing(null);
            setModalOpen(true);
            params.delete("new");
            setParams(params);
        }
    }, []);

    const moveJob = async (jobId, newStatusId) => {
        await api.put(`/jobs/${jobId}`, { status_id: newStatusId });
        load();
    };

    const deleteJob = async (jobId) => {
        if (!window.confirm("Eliminare la commessa?")) return;
        await api.delete(`/jobs/${jobId}`);
        load();
    };

    const openEdit = (job) => {
        setEditing(job);
        setModalOpen(true);
    };

    const filteredJobs = filter === "all" ? jobs :
        filter === "urgent" ? jobs.filter((j) => j.priority === "urgent") :
        jobs.filter((j) => j.priority === filter);

    return (
        <div className="space-y-6" data-testid="commesse-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h1
                        className="tracking-tight"
                        style={{
                            color: "var(--navy)",
                            fontFamily: "Geist, Outfit, sans-serif",
                            fontSize: "clamp(1.7rem, 2.8vw, 2.2rem)",
                            fontWeight: 200,
                            letterSpacing: "-0.035em",
                            lineHeight: 1.05,
                        }}
                    >
                        <span style={{ fontWeight: 600 }}>Commesse</span>
                    </h1>
                    <p className="text-[13px] mt-1" style={{ color: "var(--text-2)" }}>Trascina le card tra le colonne per cambiare stato.</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex gap-1 p-1 rounded-lg" style={{ background: "var(--surface)", border: "1px solid var(--line)" }}>
                        {[
                            { v: "all", l: "Tutte" },
                            { v: "normal", l: "Normali" },
                            { v: "high", l: "Alta" },
                            { v: "urgent", l: "Urgenti" },
                        ].map((f) => (
                            <button
                                key={f.v}
                                data-testid={`filter-${f.v}`}
                                onClick={() => setFilter(f.v)}
                                className={`px-3 py-1.5 mono text-[11px] uppercase tracking-wider font-bold rounded-md transition`}
                                style={{
                                    background: filter === f.v ? "linear-gradient(135deg, var(--mod-accent), var(--mod-accent-deep))" : "transparent",
                                    color: filter === f.v ? "#fff" : "var(--text-2)",
                                }}
                            >{f.l}</button>
                        ))}
                    </div>
                    <button data-testid="commesse-new" onClick={() => { setEditing(null); setModalOpen(true); }} className="btn-grad px-4 py-2.5 rounded-lg text-[13px] font-bold flex items-center gap-2">
                        <Plus size={14} /> Nuova commessa
                    </button>
                </div>
            </div>

            {stages.length === 0 ? (
                <div className="card-tpl p-12 text-center">
                    <AlertCircle size={28} className="mx-auto mb-3" style={{ color: "var(--orange)" }} />
                    <div className="font-bold" style={{ color: "var(--navy)" }}>Workflow non configurato</div>
                    <p className="text-[13px] mt-2" style={{ color: "var(--text-2)" }}>Vai in Configurazione per impostare gli stati del workflow.</p>
                </div>
            ) : (
                <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(220px, 1fr))`, overflowX: "auto" }}>
                    {stages.map((stage) => {
                        const colJobs = filteredJobs.filter((j) => j.status_id === stage.id);
                        return (
                            <div
                                key={stage.id}
                                data-testid={`col-${stage.id}`}
                                className="card-tpl p-3 min-h-[400px]"
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                    const id = e.dataTransfer.getData("jobId");
                                    if (id) moveJob(id, stage.id);
                                }}
                            >
                                <div className="flex items-center justify-between pb-2 mb-3" style={{ borderBottom: "1px dashed var(--line-2)" }}>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full" style={{ background: stage.color }}></div>
                                        <div className="mono text-[10px] uppercase tracking-[0.1em] font-bold" style={{ color: "var(--navy)" }}>{stage.name}</div>
                                    </div>
                                    <div className="mono text-[10px] font-bold px-1.5 py-0.5 rounded-md" style={{ color: "var(--muted)", background: "var(--bg)" }}>
                                        {colJobs.length}
                                    </div>
                                </div>

                                {colJobs.map((j) => (
                                    <div
                                        key={j.id}
                                        data-testid={`job-card-${j.id}`}
                                        draggable
                                        onDragStart={(e) => e.dataTransfer.setData("jobId", j.id)}
                                        className="card-tpl mb-2.5 p-3 cursor-grab hover:shadow-md transition-all"
                                        style={{
                                            borderLeft: j.priority === "urgent" ? "3px solid var(--mod-accent)" :
                                                        j.priority === "high" ? "3px solid var(--orange)" : "1px solid var(--line)"
                                        }}
                                    >
                                        <div className="flex items-center justify-between mb-1.5">
                                            <div className="mono text-[10px] font-bold" style={{ color: "var(--mod-accent)" }}>#{j.job_number}</div>
                                            {j.priority === "urgent" && (
                                                <span className="mono text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white" style={{ background: "var(--mod-accent)" }}>URGENTE</span>
                                            )}
                                        </div>
                                        <div className="text-[13px] font-bold leading-tight mb-1.5" style={{ color: "var(--navy)" }}>{j.title}</div>
                                        {j.customer_name && <div className="text-[11px] mb-2" style={{ color: "var(--muted)" }}>{j.customer_name}</div>}
                                        <div className="flex items-center justify-between text-[10px]">
                                            <div className="flex flex-wrap gap-1">
                                                {j.department && <span className="mono px-1.5 py-0.5 rounded uppercase tracking-wider font-semibold" style={{ background: "var(--cobalt-soft)", color: "var(--cobalt)" }}>{j.department}</span>}
                                            </div>
                                            {j.deadline && <span className="mono" style={{ color: j.priority === "urgent" ? "var(--mod-accent)" : "var(--muted)" }}>▸ {j.deadline}</span>}
                                        </div>
                                        <div className="flex items-center gap-1 mt-2 pt-2" style={{ borderTop: "1px dashed var(--line)" }}>
                                            <button onClick={() => openEdit(j)} className="text-[10px] mono uppercase tracking-wider hover:underline" style={{ color: "var(--cobalt)" }}>Modifica</button>
                                            <button onClick={() => deleteJob(j.id)} className="ml-auto text-[10px] mono uppercase tracking-wider hover:underline" style={{ color: "var(--red)" }}>
                                                <Trash2 size={11} className="inline" />
                                            </button>
                                        </div>
                                    </div>
                                ))}

                                {colJobs.length === 0 && (
                                    <div className="text-center py-6 text-[11px] italic" style={{ color: "var(--muted-2)" }}>Nessuna commessa</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <JobModal
                open={modalOpen}
                onClose={() => { setModalOpen(false); setEditing(null); }}
                onSaved={load}
                stages={stages}
                customers={customers}
                editing={editing}
            />
        </div>
    );
}
